import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';

// Define office layout outside component (static data)
const LOCATIONS = [
  { name: 'Data Science', gridX: 1, gridY: 1, color: '#FFD700', info: 'Data Science Department: Where we crunch numbers and uncover insights.' },
  { name: 'Development', gridX: 3, gridY: 1, color: '#90EE90', info: 'Development Department: Building innovative software solutions.' },
  { name: 'Executive', gridX: 5, gridY: 1, color: '#ADD8E6', info: 'Executive Suite: Leadership and strategy hub.' },
  { name: 'Coffee', gridX: 2, gridY: 3, color: '#D2691E', info: 'Coffee Area: Grab a brew and chat about projects.' },
  { name: 'Foosball', gridX: 4, gridY: 3, color: '#FF4500', info: 'Foosball Table: Fun breaks to boost creativity.' }
];

// Wall pictures as hotspots
const PICTURES = [
  { name: 'Elephant Picture', gridX: 0, gridY: 2, color: '#FFFF00', info: 'How to Eat an Elephant: One bite at a time! (Yellow elephant with a bite taken out)', isPicture: true },
  { name: 'Fish Picture', gridX: 6, gridY: 2, color: '#0000FF', info: 'Blue Fish Swimming Together: Symbolizing teamwork and unity.', isPicture: true }
];

export const VirtualOfficeExplorer = () => {
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const [playerPos, setPlayerPos] = useState({ gridX: 3, gridY: 3 }); // Starting position, center-ish
  const [currentHotspot, setCurrentHotspot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [dialogPosition, setDialogPosition] = useState({ top: '50%', left: '50%' });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Isometric constants
  const TILE_WIDTH = 60;
  const TILE_HEIGHT = 30;
  const OFFSET_X = 400; // Center offset
  const OFFSET_Y = 100;

  // Function to convert grid (x, y) to isometric screen coordinates
  const toIso = useCallback((x, y) => {
    return {
      screenX: OFFSET_X + (x - y) * (TILE_WIDTH / 2),
      screenY: OFFSET_Y + (x + y) * (TILE_HEIGHT / 2)
    };
  }, [TILE_WIDTH, TILE_HEIGHT, OFFSET_X, OFFSET_Y]);

  // Combine all interactables
  const hotspots = useMemo(() => [...LOCATIONS, ...PICTURES], []);

  // Draw floor grid
  const drawGrid = useCallback((ctx) => {
    ctx.strokeStyle = '#ccc';
    for (let x = 0; x <= 6; x++) {
      for (let y = 0; y <= 6; y++) {
        const { screenX, screenY } = toIso(x, y);
        ctx.beginPath();
        ctx.moveTo(screenX - TILE_WIDTH / 2, screenY);
        ctx.lineTo(screenX, screenY - TILE_HEIGHT / 2);
        ctx.lineTo(screenX + TILE_WIDTH / 2, screenY);
        ctx.lineTo(screenX, screenY + TILE_HEIGHT / 2);
        ctx.closePath();
        ctx.stroke();
      }
    }
  }, [toIso, TILE_WIDTH, TILE_HEIGHT]);

  // Draw improved isometric desk with person and laptop for teams
  const drawTeamDesk = useCallback((ctx, screenX, screenY) => {
    // Desk base (isometric rectangle)
    ctx.fillStyle = '#8B4513'; // Brown wood
    ctx.beginPath();
    ctx.moveTo(screenX - 20, screenY - 10);
    ctx.lineTo(screenX, screenY - 20);
    ctx.lineTo(screenX + 20, screenY - 10);
    ctx.lineTo(screenX, screenY);
    ctx.closePath();
    ctx.fill();

    // Person (simplified: head, body, arms in sitting pose)
    ctx.fillStyle = '#FFA07A'; // Skin tone
    ctx.beginPath();
    ctx.arc(screenX - 5, screenY - 25, 5, 0, Math.PI * 2); // Head
    ctx.fill();
    ctx.fillStyle = '#00008B'; // Shirt
    ctx.beginPath();
    ctx.moveTo(screenX - 10, screenY - 20);
    ctx.lineTo(screenX + 5, screenY - 20);
    ctx.lineTo(screenX + 5, screenY - 10);
    ctx.lineTo(screenX - 10, screenY - 10);
    ctx.closePath();
    ctx.fill();
    // Arms (simple lines)
    ctx.strokeStyle = '#00008B';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenX - 10, screenY - 15);
    ctx.lineTo(screenX - 15, screenY - 10);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(screenX + 5, screenY - 15);
    ctx.lineTo(screenX + 10, screenY - 10);
    ctx.stroke();

    // Laptop (open screen and base)
    ctx.fillStyle = '#A9A9A9'; // Gray laptop
    ctx.beginPath();
    ctx.moveTo(screenX + 5, screenY - 15);
    ctx.lineTo(screenX + 15, screenY - 20);
    ctx.lineTo(screenX + 15, screenY - 10);
    ctx.lineTo(screenX + 5, screenY - 5);
    ctx.closePath();
    ctx.fill();
    // Screen glow
    ctx.fillStyle = '#87CEEB';
    ctx.fillRect(screenX + 6, screenY - 18, 8, 6); // Small screen area
  }, []);

  // Draw rooms/areas as raised blocks, with improved graphics for teams
  const drawLocations = useCallback((ctx) => {
    hotspots.forEach(spot => {
      const { screenX, screenY } = toIso(spot.gridX, spot.gridY);
      if (spot.isPicture) {
        // Pictures as smaller rectangles on "walls" (improved with border)
        ctx.fillStyle = spot.color;
        ctx.fillRect(screenX - 20, screenY - 40, 40, 30);
        ctx.strokeStyle = '#000';
        ctx.strokeRect(screenX - 20, screenY - 40, 40, 30);
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.fillText(spot.name[0], screenX - 10, screenY - 25); // Initial as placeholder
      } else if (spot.name === 'Data Science' || spot.name === 'Development') {
        // Improved team graphics
        drawTeamDesk(ctx, screenX, screenY, spot.color);
        ctx.fillStyle = '#000';
        ctx.font = '12px Arial';
        ctx.fillText(spot.name, screenX - 40, screenY + 10); // Label below
      } else {
        // Standard rooms as diamonds (with slight improvement: shadow)
        ctx.fillStyle = spot.color;
        ctx.beginPath();
        ctx.moveTo(screenX, screenY - TILE_HEIGHT);
        ctx.lineTo(screenX + TILE_WIDTH / 2, screenY - TILE_HEIGHT / 2);
        ctx.lineTo(screenX, screenY);
        ctx.lineTo(screenX - TILE_WIDTH / 2, screenY - TILE_HEIGHT / 2);
        ctx.closePath();
        ctx.fill();
        // Shadow for depth
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.beginPath();
        ctx.moveTo(screenX, screenY);
        ctx.lineTo(screenX + TILE_WIDTH / 2, screenY - TILE_HEIGHT / 2);
        ctx.lineTo(screenX + TILE_WIDTH / 2, screenY + TILE_HEIGHT / 2);
        ctx.lineTo(screenX, screenY + TILE_HEIGHT);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.font = '12px Arial';
        ctx.fillText(spot.name, screenX - 40, screenY - 5);
      }
      // Store bounding box for clicks (approximate, adjusted for size)
      spot.bounds = {
        minX: screenX - TILE_WIDTH / 2,
        maxX: screenX + TILE_WIDTH / 2,
        minY: screenY - TILE_HEIGHT * 1.5, // Extended for taller elements
        maxY: screenY + TILE_HEIGHT / 2
      };
    });
  }, [hotspots, toIso, drawTeamDesk, TILE_WIDTH, TILE_HEIGHT]);

  // Draw player avatar (improved to simple person figure)
  const drawPlayer = useCallback((ctx) => {
    const { screenX, screenY } = toIso(playerPos.gridX, playerPos.gridY);
    // Head
    ctx.fillStyle = '#FFDAB9';
    ctx.beginPath();
    ctx.arc(screenX, screenY - TILE_HEIGHT - 5, 5, 0, Math.PI * 2);
    ctx.fill();
    // Body
    ctx.fillStyle = '#228B22';
    ctx.beginPath();
    ctx.moveTo(screenX - 5, screenY - TILE_HEIGHT);
    ctx.lineTo(screenX + 5, screenY - TILE_HEIGHT);
    ctx.lineTo(screenX + 5, screenY - TILE_HEIGHT + 10);
    ctx.lineTo(screenX - 5, screenY - TILE_HEIGHT + 10);
    ctx.closePath();
    ctx.fill();
  }, [playerPos, toIso, TILE_HEIGHT]);

  // Simple bot response logic
  const getBotResponse = (userMessage, spotName) => {
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('hi') || lowerMessage.includes('hello')) {
      return `Hello! I'm from the ${spotName}. How can I help you learn about us?`;
    } else if (lowerMessage.includes('what do you do')) {
      return `In ${spotName}, we focus on innovative solutions. For example: ${currentHotspot.info}`;
    } else {
      return `That's a great question about ${spotName}! Feel free to ask more specifics.`;
    }
  };

  // Handle sending message
  const handleSend = () => {
    if (inputValue.trim() === '') return;
    setMessages((prev) => [...prev, { role: 'user', text: inputValue }]);
    const botReply = getBotResponse(inputValue, currentHotspot.name);
    setMessages((prev) => [...prev, { role: 'bot', text: botReply }]);
    setInputValue('');
  };

  // Handle key press in input (enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  // Effect for rendering when player position changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Render function
    const render = (ctx) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      drawGrid(ctx);
      drawLocations(ctx);
      drawPlayer(ctx);
    };
    
    render(ctx);
  }, [playerPos, drawGrid, drawLocations, drawPlayer]);

  // Effect for setting up click and key listeners
  useEffect(() => {
    const canvas = canvasRef.current;

    // Click handler (optional, kept for direct clicks)
    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      hotspots.forEach(spot => {
        if (clickX >= spot.bounds.minX && clickX <= spot.bounds.maxX &&
            clickY >= spot.bounds.minY && clickY <= spot.bounds.maxY) {
          setCurrentHotspot(spot);
          setMessages([{ role: 'bot', text: spot.info }]);
        }
      });
    };

    canvas.addEventListener('click', handleClick);

    // Keydown handler for movement
    const handleKeyDown = (e) => {
      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault(); // Prevent page scrolling when using arrow keys
      }
      setPlayerPos((prevPos) => {
        let newX = prevPos.gridX;
        let newY = prevPos.gridY;
        switch (e.key) {
          case 'ArrowRight':
            newX = Math.min(6, newX + 1);
            break;
          case 'ArrowLeft':
            newX = Math.max(0, newX - 1);
            break;
          case 'ArrowUp':
            newY = Math.max(0, newY - 1);
            break;
          case 'ArrowDown':
            newY = Math.min(6, newY + 1);
            break;
          default:
            return prevPos;
        }
        // Check for hotspot overlap
        const overlappingSpot = hotspots.find(spot => spot.gridX === newX && spot.gridY === newY);
        if (overlappingSpot && !currentHotspot) {
          setCurrentHotspot(overlappingSpot);
          setMessages([{ role: 'bot', text: overlappingSpot.info }]);
        } else if (currentHotspot && (newX !== currentHotspot.gridX || newY !== currentHotspot.gridY)) {
          setCurrentHotspot(null);
          setMessages([]);
        }
        return { gridX: newX, gridY: newY };
      });
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      canvas.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentHotspot, hotspots]);

  // Focus input when dialog opens
  useEffect(() => {
    if (currentHotspot && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentHotspot]);

  // Dragging handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setDialogPosition({
        top: `${e.clientY - dragOffset.y}px`,
        left: `${e.clientX - dragOffset.x}px`
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e) => {
    if (e.target.tagName === 'H2') { // Only drag from title
      setIsDragging(true);
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        style={{ border: '1px solid #000', background: '#fff' }}
      />
      {currentHotspot && (
        <div
          ref={dialogRef}
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: dialogPosition.top,
            left: dialogPosition.left,
            transform: 'translate(-50%, -50%)',
            background: 'white',
            border: '2px solid black',
            padding: '20px',
            width: '400px',
            height: '400px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10,
            cursor: 'move'
          }}
        >
          <h2 style={{ color: 'black', margin: 0, cursor: 'grab' }}>{currentHotspot.name}</h2>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '10px',
              border: '1px solid #ccc',
              padding: '10px',
            }}
          >
            {messages.map((msg, index) => (
              <div key={index} style={{ textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                <strong>{msg.role === 'user' ? 'You: ' : 'Bot: '}</strong>{msg.text}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex' }}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{ flex: 1, marginRight: '10px' }}
            />
            <button onClick={handleSend}>Send</button>
          </div>
          <button
            onClick={() => {
              setCurrentHotspot(null);
              setMessages([]);
            }}
            style={{ marginTop: '10px' }}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};

export default VirtualOfficeExplorer;

// Usage: Import and use in your App.js like <VirtualOfficeExplorer />
// TODO for next iterations:
// - Enhance bot responses with more sophisticated logic or integration
// - Improve modal styling (e.g., with CSS libraries if allowed)
// - Add scrolling to bottom of chat on new messages
// - Draggable view or zoom
// - Animations on hover/click/movement
// - Collision detection or path restrictions